/**
 * Asset types for the Gotti Sandbox
 * 
 * Assets are stored separately from code files in a protected /assets folder.
 * They are content-addressable: the SHA-256 hash of the binary content 
 * determines the storage key, enabling deduplication across projects.
 * 
 * Storage:
 * - S3: assets/<hash>.<ext>
 * - Export (.gbox): includes inlined base64 + hash for reconstruction
 */

/**
 * Supported asset types with their MIME types and extensions
 */
export const ASSET_TYPES = {
  // Images
  'image/png': { ext: 'png', category: 'image' },
  'image/jpeg': { ext: 'jpg', category: 'image' },
  'image/gif': { ext: 'gif', category: 'image' },
  'image/webp': { ext: 'webp', category: 'image' },
  'image/svg+xml': { ext: 'svg', category: 'image' },
  
  // Audio
  'audio/mpeg': { ext: 'mp3', category: 'audio' },
  'audio/ogg': { ext: 'ogg', category: 'audio' },
  'audio/wav': { ext: 'wav', category: 'audio' },
  'audio/webm': { ext: 'webm', category: 'audio' },
  
  // JSON data (sprite sheets, tilemaps, etc.)
  'application/json': { ext: 'json', category: 'data' },
} as const;

export type AssetMimeType = keyof typeof ASSET_TYPES;
export type AssetCategory = 'image' | 'audio' | 'data';

/**
 * Maximum asset size in bytes (10MB)
 */
export const MAX_ASSET_SIZE = 10 * 1024 * 1024;

/**
 * Asset folder name - this folder cannot be deleted
 */
export const ASSETS_FOLDER_NAME = 'assets';

/**
 * Represents a reference to an asset stored in S3
 * Used in the GboxDocument when the sandbox is persisted
 */
export interface AssetReference {
  /** SHA-256 hash of the asset content (hex, 64 chars) */
  hash: string;
  /** Original filename */
  name: string;
  /** MIME type */
  mimeType: AssetMimeType;
  /** File size in bytes */
  size: number;
  /** CDN URL for lazy loading (set by backend) */
  url?: string;
}

/**
 * Asset with inlined data for export (.gbox files)
 * When exporting, we include the base64 data so the file is self-contained
 */
export interface ExportedAsset {
  /** SHA-256 hash of the asset content */
  hash: string;
  /** Original filename */
  name: string;
  /** MIME type */
  mimeType: AssetMimeType;
  /** File size in bytes */
  size: number;
  /** Base64-encoded asset data */
  base64: string;
}

/**
 * Asset metadata stored in the sandbox
 * This is what the frontend works with
 */
export interface SandboxAsset {
  /** SHA-256 hash of the asset content */
  hash: string;
  /** Original filename (editable by user) */
  name: string;
  /** MIME type */
  mimeType: AssetMimeType;
  /** File size in bytes */
  size: number;
  /** Optional subfolder within /assets (e.g., "sprites", "sounds") */
  folder?: string;
  /** CDN URL for loading the asset */
  url?: string;
  /** Creation timestamp */
  createdAt: string;
  /** Optional description/notes */
  description?: string;
}

/**
 * Asset folder structure in the GboxDocument
 */
export interface GboxAssets {
  /** Version for forward compatibility */
  version: 1;
  /** Flat list of all assets with their references */
  items: AssetReference[];
}

/**
 * Asset folder structure for export (with inlined data)
 */
export interface GboxAssetsExport {
  /** Version for forward compatibility */
  version: 1;
  /** Assets with inlined base64 data */
  items: ExportedAsset[];
}

// ============================================================================
// API Types
// ============================================================================

/**
 * Request to upload an asset
 */
export interface UploadAssetRequest {
  /** Original filename */
  name: string;
  /** MIME type */
  mimeType: AssetMimeType;
  /** Base64-encoded asset data */
  base64: string;
  /** Optional subfolder within /assets */
  folder?: string;
  /** Optional description */
  description?: string;
}

/**
 * Response from uploading an asset
 */
export interface UploadAssetResponse {
  /** The asset reference */
  asset: AssetReference;
  /** Whether this asset already existed (deduplication) */
  deduplicated: boolean;
}

/**
 * Request to get multiple assets by their hashes
 */
export interface GetAssetsRequest {
  /** Array of asset hashes to retrieve */
  hashes: string[];
}

/**
 * Response with signed URLs for assets
 */
export interface GetAssetsResponse {
  /** Map of hash -> URL */
  urls: Record<string, string>;
  /** Hashes that were not found */
  notFound: string[];
}

/**
 * Request to import assets from an exported gbox
 */
export interface ImportAssetsRequest {
  /** Exported assets with base64 data */
  assets: ExportedAsset[];
}

/**
 * Response from importing assets
 */
export interface ImportAssetsResponse {
  /** Successfully imported assets */
  imported: AssetReference[];
  /** Assets that failed to import */
  failed: Array<{ name: string; error: string }>;
}

// ============================================================================
// Utilities
// ============================================================================

/**
 * Check if a MIME type is supported
 */
export function isSupportedAssetType(mimeType: string): mimeType is AssetMimeType {
  return mimeType in ASSET_TYPES;
}

/**
 * Get the file extension for a MIME type
 */
export function getAssetExtension(mimeType: AssetMimeType): string {
  return ASSET_TYPES[mimeType].ext;
}

/**
 * Get the category for a MIME type
 */
export function getAssetCategory(mimeType: AssetMimeType): AssetCategory {
  return ASSET_TYPES[mimeType].category;
}

/**
 * Validate an asset hash format (SHA-256 hex, 64 chars)
 */
export function isValidAssetHash(hash: string): boolean {
  return /^[a-f0-9]{64}$/i.test(hash);
}

/**
 * Get the S3 key for an asset
 */
export function getAssetStorageKey(hash: string, mimeType: AssetMimeType): string {
  const ext = getAssetExtension(mimeType);
  return `assets/${hash}.${ext}`;
}

/**
 * Create an empty GboxAssets structure
 */
export function createEmptyGboxAssets(): GboxAssets {
  return {
    version: 1,
    items: [],
  };
}

/**
 * Create an empty GboxAssetsExport structure
 */
export function createEmptyGboxAssetsExport(): GboxAssetsExport {
  return {
    version: 1,
    items: [],
  };
}

/**
 * Convert AssetReference to ExportedAsset (requires fetching base64 data)
 */
export function assetRefToExport(
  ref: AssetReference,
  base64: string
): ExportedAsset {
  return {
    hash: ref.hash,
    name: ref.name,
    mimeType: ref.mimeType,
    size: ref.size,
    base64,
  };
}

/**
 * Convert ExportedAsset to AssetReference (strips base64)
 */
export function exportToAssetRef(exported: ExportedAsset, url?: string): AssetReference {
  return {
    hash: exported.hash,
    name: exported.name,
    mimeType: exported.mimeType,
    size: exported.size,
    url,
  };
}

// ============================================================================
// Folder Protection & Path Utilities
// ============================================================================

/**
 * Protected folders that cannot be deleted.
 * The assets folder is always protected.
 */
export const PROTECTED_FOLDERS = [ASSETS_FOLDER_NAME] as const;

/**
 * Check if a folder path is protected and cannot be deleted
 */
export function isProtectedFolder(path: string): boolean {
  const normalizedPath = path.replace(/^\/+|\/+$/g, '').toLowerCase();
  return PROTECTED_FOLDERS.some(folder => 
    normalizedPath === folder || normalizedPath.startsWith(`${folder}/`)
  );
}

/**
 * Check if a path is within the assets folder
 */
export function isAssetPath(path: string): boolean {
  const normalizedPath = path.replace(/^\/+|\/+$/g, '').toLowerCase();
  return normalizedPath === ASSETS_FOLDER_NAME || 
         normalizedPath.startsWith(`${ASSETS_FOLDER_NAME}/`);
}

/**
 * Get the subfolder path within assets (e.g., "sprites/player.png" -> "sprites")
 */
export function getAssetSubfolder(assetPath: string): string | null {
  const normalizedPath = assetPath.replace(/^\/+|\/+$/g, '');
  if (!normalizedPath.toLowerCase().startsWith(`${ASSETS_FOLDER_NAME}/`)) {
    return null;
  }
  
  const relativePath = normalizedPath.slice(ASSETS_FOLDER_NAME.length + 1);
  const lastSlash = relativePath.lastIndexOf('/');
  
  if (lastSlash === -1) {
    return ''; // Root of assets folder
  }
  
  return relativePath.slice(0, lastSlash);
}

/**
 * Build the full path for an asset
 */
export function buildAssetPath(name: string, folder?: string): string {
  if (folder) {
    return `${ASSETS_FOLDER_NAME}/${folder}/${name}`;
  }
  return `${ASSETS_FOLDER_NAME}/${name}`;
}

/**
 * Result of validating an asset operation
 */
export interface AssetValidationResult {
  valid: boolean;
  error?: string;
}

/**
 * Validate an asset file before upload
 */
export function validateAssetFile(
  name: string,
  mimeType: string,
  size: number
): AssetValidationResult {
  // Check name
  if (!name || name.length === 0) {
    return { valid: false, error: 'Asset name is required' };
  }
  
  if (name.length > 255) {
    return { valid: false, error: 'Asset name must be 255 characters or less' };
  }
  
  // Check for invalid characters (basic sanitization)
  if (/[<>:"|?*\\]/.test(name)) {
    return { valid: false, error: 'Asset name contains invalid characters' };
  }
  
  // Check MIME type
  if (!isSupportedAssetType(mimeType)) {
    return { 
      valid: false, 
      error: `Unsupported file type: ${mimeType}. Supported types: ${Object.keys(ASSET_TYPES).join(', ')}` 
    };
  }
  
  // Check size
  if (size <= 0) {
    return { valid: false, error: 'Asset file is empty' };
  }
  
  if (size > MAX_ASSET_SIZE) {
    return { 
      valid: false, 
      error: `Asset exceeds maximum size of ${MAX_ASSET_SIZE / (1024 * 1024)}MB` 
    };
  }
  
  return { valid: true };
}

/**
 * Get a suggested subfolder for an asset based on its type
 */
export function suggestAssetFolder(mimeType: AssetMimeType): string {
  const category = getAssetCategory(mimeType);
  switch (category) {
    case 'image':
      return 'images';
    case 'audio':
      return 'sounds';
    case 'data':
      return 'data';
    default:
      return '';
  }
}
